/**
 * chartTheme — Recharts chart chrome (grid / axes / tooltip / legend) sourced
 * from the design tokens in index.css, so every chart follows the active theme
 * instead of hardcoding dark-mode hexes (#1e293b, #94a3b8, …).
 *
 * Token sources (index.css :root / html.light — RGB triplets):
 *   --border          → grid lines            (dark fallback 38 38 44   ≈ #26262c)
 *   --text-secondary  → axis ticks & labels   (dark fallback 148 163 184 = #94a3b8)
 *   --bg-surface      → tooltip background    (dark fallback 15 15 17)
 *   --text-primary    → tooltip text          (dark fallback 241 245 249)
 *
 * SVG presentation attributes (stroke/fill on grid lines and axis ticks) do not
 * resolve `var()`, so those values are resolved once via getComputedStyle with
 * SSR-safe fallbacks. Tooltip/legend styles land on HTML elements, where
 * `rgb(var(--token))` works and stays live across theme toggles.
 */

import type { CSSProperties } from 'react'

// Dark-theme fallbacks — must mirror the :root triplets in index.css.
const TOKEN_FALLBACKS: Record<string, string> = {
  '--border': '38 38 44',
  '--text-secondary': '148 163 184',
  '--bg-surface': '15 15 17',
  '--text-primary': '241 245 249',
}

/** Resolve a CSS custom property (RGB triplet) to an rgb() string, SSR-safe. */
function tokenRgb(varName: string): string {
  let triplet = TOKEN_FALLBACKS[varName] ?? '148 163 184'
  if (typeof document !== 'undefined' && typeof window !== 'undefined') {
    const live = window
      .getComputedStyle(document.documentElement)
      .getPropertyValue(varName)
      .trim()
    if (live) triplet = live
  }
  return `rgb(${triplet})`
}

/** CartesianGrid / PolarGrid stroke — recessive, from --border. */
export const gridStroke = tokenRgb('--border')

/** Axis tick text fill + axis line stroke — from --text-secondary. */
export const axisTickFill = tokenRgb('--text-secondary')

/** Recharts <Tooltip contentStyle> — surface card chrome from tokens (live var()). */
export const tooltipContentStyle: CSSProperties = {
  background: 'rgb(var(--bg-surface))',
  border: '1px solid rgb(var(--border))',
  borderRadius: 8,
  color: 'rgb(var(--text-primary))',
  fontSize: 12,
}

/** Recharts <Tooltip labelStyle> — primary ink for the tooltip label row. */
export const tooltipLabelStyle: CSSProperties = {
  color: 'rgb(var(--text-primary))',
  fontWeight: 600,
}

/** Recharts <Legend wrapperStyle> — the compact legend the pages repeat. */
export const legendWrapperStyle: CSSProperties = {
  fontSize: 11,
  paddingTop: 8,
  color: 'rgb(var(--text-secondary))',
}
